import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import { get } from "lodash";

import { getOptionLabel } from "common/enums";
import { ProfileImage } from "Components/ApplicationViewModal/style";
import Modal from "Components/Common/Modal";

import { getImageUrl } from "utils";

import { useLazyGetAuthorizedUserQuery, useLazyGetUserQuery } from "apis/User";
import { useLazyGetLatestAuthorizedUser } from "apis/UtilityConsumer/utilityConsumer";

const fields = [
  {
    name: "Salutation",
    key: "salutation",
    renderField: (value) => getOptionLabel("salutationOptions", value) || "-",
    isRequired: true,
  },
  {
    name: "First Name",
    key: "firstName",
    isRequired: true,
  },
  {
    name: "Middle Name",
    key: "middleName",
    isRequired: true,
  },
  {
    name: "Last Name",
    key: "lastName",
    isRequired: true,
  },
  {
    name: "Email",
    key: "email",
    isRequired: true,
  },
  {
    name: "Mobile Number",
    key: "mobileNumber",
    isRequired: true,
  },
  {
    name: "Designation",
    key: "designation",
    isRequired: false,
  },
  {
    name: "Address Line 1",
    key: "addressLine1",
    isRequired: true,
  },
  {
    name: "Address Line 2",
    key: "addressLine2",
    isRequired: false,
  },
  {
    name: "City",
    key: "city.name",
    isRequired: true,
  },
  {
    name: "Pincode",
    key: "pinCode",
    isRequired: true,
  },
];

const ViewUserDetails = ({
  userId,
  handleClose,
  isFetchingNewAuthorized = false,
  isViewingFromAuthorizedUsersPage = false,
  t,
}) => {
  const [getUserData, { data: { data: userData } = {} }] =
    useLazyGetUserQuery();
 
    const [getAuthorizedUserData, { data: { data: authorizedUserDetails } = {} }] =
    useLazyGetAuthorizedUserQuery();

  const [getLatestAuthorizedUser, { data: { data: authorizedUserData } = {} }] =
    useLazyGetLatestAuthorizedUser();

  const userDetails = useMemo(
    () => userData ?? authorizedUserData ?? authorizedUserDetails,
    [userData, authorizedUserData,authorizedUserDetails]
  );

  useEffect(() => {
    if (isFetchingNewAuthorized) {
      getLatestAuthorizedUser(userId);
    } else if (isViewingFromAuthorizedUsersPage) {
      getAuthorizedUserData(userId);
    } else {
      getUserData(userId);
    }
  }, []);

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title={t("User Details")}
      maxWidth="md"
    >
      <div className="py-4">
        <ProfileImage
          className="mb-4"
          src={getImageUrl(userDetails?.profileImage)}
        />

        <Row className="d-flex">
          {fields.map(({ name, key, isRequired, renderField }) => {
            const value = get(userDetails, key, "");

            return (
              <Col key={key} lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t(name)}:</Col>
                  <Col
                    md={7}
                    className="fw-semibold"
                    style={{ wordBreak: "break-word" }}
                  >
                    {renderField
                      ? renderField(value)
                      : get(userDetails, key, null) ||
                        (isRequired ? "-" : "N/A")}
                  </Col>
                </Row>
              </Col>
            );
          })}
        </Row>
      </div>
    </Modal>
  );
};

ViewUserDetails.propTypes = {
  userId: PropTypes.number,
  handleClose: PropTypes.func,
  isFetchingNewAuthorized: PropTypes.bool,
  isViewingFromAuthorizedUsersPage: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(ViewUserDetails);
